/**
 * Phase 2 manual-trading API — per-account discretionary orders + the manual
 * ledger. Backs the manual-arm toggle, manual-positions card, the (un-gated)
 * order ticket, and flatten-manual. All operator-auth'd under /admin.
 *
 * The manual-ARM is the fail-closed interlock: an order is allowed only when
 * hard-freeze OFF + global live ON + the account is manual-armed. Reads are safe;
 * arm/order/flatten are audited money-or-state actions.
 */
import { z } from 'zod';

import type { ApiResult } from './client';
import { get, post } from './client';

export const manualArmSchema = z.object({
  broker_account_id: z.string(),
  armed: z.boolean(),
  armed_until: z.string().nullable().optional(),
  armed_by: z.string().nullable().optional(),
  armed_at: z.string().nullable().optional(),
  global_live_enabled: z.boolean(),
  order_allowed: z.boolean(),
  gate_reason: z.string(),
});
export type ManualArm = z.infer<typeof manualArmSchema>;

export const manualPositionSchema = z.object({
  symbol: z.string(),
  side: z.string(),
  quantity: z.number(),
  avg_price: z.number(),
  realized_pnl: z.number(),
});
export type ManualPosition = z.infer<typeof manualPositionSchema>;
export const manualPositionsSchema = z.array(manualPositionSchema);

export const manualOrderResultSchema = z.object({
  status: z.string(),
  broker_order_id: z.string().nullable().optional(),
  filled_qty: z.number(),
  avg_price: z.number().nullable().optional(),
  net_qty: z.number(),
  recorded: z.boolean(),
});
export type ManualOrderResult = z.infer<typeof manualOrderResultSchema>;

export const flattenManualLegSchema = z.object({
  symbol: z.string(),
  closed: z.boolean(),
  filled_qty: z.number(),
  error: z.string().nullable().optional(),
});
export const flattenManualSchema = z.array(flattenManualLegSchema);
export type FlattenManualLeg = z.infer<typeof flattenManualLegSchema>;

const enc = encodeURIComponent;

export const getManualArm = (
  accountId: string, signal?: AbortSignal,
): Promise<ApiResult<ManualArm>> =>
  get(`/admin/broker-accounts/${enc(accountId)}/manual-arm`, manualArmSchema, { signal });

export const setManualArm = (
  accountId: string,
  body: { armed: boolean; reason: string; duration_seconds?: number },
): Promise<ApiResult<ManualArm>> =>
  post(`/admin/broker-accounts/${enc(accountId)}/manual-arm`, manualArmSchema, body);

export const getManualPositions = (
  accountId: string, signal?: AbortSignal,
): Promise<ApiResult<ManualPosition[]>> =>
  get(`/admin/broker-accounts/${enc(accountId)}/manual-positions`, manualPositionsSchema, { signal });

export const placeManualOrder = (
  accountId: string,
  body: {
    symbol: string; side: 'BUY' | 'SELL'; quantity: number;
    product_type?: string; order_type?: 'MARKET' | 'LIMIT';
    limit_price?: number; ref_price?: number; reason: string;
  },
): Promise<ApiResult<ManualOrderResult>> =>
  post(`/admin/broker-accounts/${enc(accountId)}/manual-order`, manualOrderResultSchema, body, {
    // 50s > the backend's ~40s broker-confirm bound. NEVER auto-retry an order
    // placement: a timed-out first attempt may still fill, and the retry either
    // double-places or trips the server's duplicate-order guard mid-flight.
    retry: { attempts: 1 },
    timeoutMs: 50_000,
  });

export const flattenManual = (
  accountId: string, body: { reason: string },
): Promise<ApiResult<FlattenManualLeg[]>> =>
  post(`/admin/broker-accounts/${enc(accountId)}/flatten-manual`, flattenManualSchema, body, {
    // Legs close sequentially (~40s worst each) — mirror flattenAccount's bound.
    // Never abort mid-closes: a retried flatten re-places closing orders.
    retry: { attempts: 1 },
    timeoutMs: 190_000,
  });
